import React, { FC } from "react";
import { useStyles } from "src/theme/styles";
import { useTranslation } from "react-i18next";
import type {
	LicenseLimitData,
	LicenseCalculatedData,
} from "../../types/license";
import { LicenseInsightItem } from "src/components/license";
import { Typography } from "@mui/material";

interface LicenseInsightViewProps {
	showContractNumber: boolean;
	showOnlyCalculatedValues: boolean;
	licenseLimitData?: LicenseLimitData;
	licenseCalculatedData?: LicenseCalculatedData;
	poolUsageLimitData?: LicenseLimitData;
	poolUsageCalculatedData?: LicenseCalculatedData;
}
const LicenseInsightView: FC<LicenseInsightViewProps> = (props) => {
	const { t } = useTranslation();
	const classes = useStyles();

	const getRenewalDate = () => {
		if (!props.licenseLimitData) {
			return "";
		}

		const date = new Date(props.licenseLimitData.contractDateUntil);
		const day = date.getDate();
		const month = date.getMonth() + 1;
		const year = date.getFullYear();

		return `${day}/${month}/${year}`;
	};

	const getInsightItem = (
		limitValue: number,
		calculatedValue: number,
		poolLimitValue: number,
		poolCalculatedValue: number,
		label: string
	) => {
		if (props.showOnlyCalculatedValues) {
			return (
				<LicenseInsightItem
					isSingleValue
					singleValue={calculatedValue}
					poolLimitValue={poolLimitValue}
					poolCalculatedValue={poolCalculatedValue}
					label={label}
					isDefaultColor={true}
				/>
			);
		}

		return (
			<LicenseInsightItem
				limitValue={limitValue}
				calculatedValue={calculatedValue}
				poolLimitValue={poolLimitValue}
				poolCalculatedValue={poolCalculatedValue}
				label={label}
			/>
		);
	};

	return (
		<React.Fragment>
			{props.showContractNumber && (
				<Typography
					color="textPrimary"
					variant="body2"
					className={classes.insightText}
				>{`${t("License.ContractNumber")} ${
					props.licenseLimitData?.contractNumber ?? ""
				}`}</Typography>
			)}
			<Typography
				color="textPrimary"
				variant="body2"
				style={{ display: "none" }}
			>{`${t(
				"License.ContractRenewalDate"
			)} ${getRenewalDate()}`}</Typography>
			<div className={classes.insightListContainer}>
				{getInsightItem(
					props.licenseLimitData?.agentsProVoice ?? 0,
					props.licenseCalculatedData.agentsProVoice,
					props.poolUsageLimitData?.agentsProVoice,
					props.poolUsageCalculatedData?.agentsProVoice,
					t("License.Pro")
				)}
				{getInsightItem(
					props.licenseLimitData?.agentsProOmni ?? 0,
					props.licenseCalculatedData.agentsProOmni,
					props.poolUsageLimitData?.agentsProOmni,
					props.poolUsageCalculatedData?.agentsProOmni,
					t("License.ProOmni")
				)}
				{getInsightItem(
					props.licenseLimitData?.supervisors ?? 0,
					props.licenseCalculatedData.supervisors,
					props.poolUsageLimitData?.supervisors,
					props.poolUsageCalculatedData?.supervisors,
					t("License.Supervisors")
				)}
				{getInsightItem(
					props.licenseLimitData?.operators ?? 0,
					props.licenseCalculatedData.operators,
					props.poolUsageLimitData?.operators,
					props.poolUsageCalculatedData?.operators,
					t("License.Operators")
				)}
				{getInsightItem(
					props.licenseLimitData?.agentsAiOmni ?? 0,
					props.licenseCalculatedData.agentsAiOmni,
					props.poolUsageLimitData?.agentsAiOmni,
					props.poolUsageCalculatedData?.agentsAiOmni,
					t("License.AgentsAiOmni")
				)}
				<LicenseInsightItem
					isSingleValue
					singleValue={props.licenseCalculatedData.admins}
					label={t("License.Admins")}
				/>
				<LicenseInsightItem
					isSingleValue
					singleValue={props.licenseLimitData?.aicCredits}
					label={t("License.AICCredits")}
				/>
				<LicenseInsightItem
					isSingleValue
					singleValue={props.licenseCalculatedData.totalUsers}
					label={t("License.Total")}
				/>
				<LicenseInsightItem
					hasInsightsLicense={
						props.licenseLimitData?.hasInsightsLicense ?? false
					}
					label={t("License.Insights")}
				/>
			</div>
		</React.Fragment>
	);
};

export default LicenseInsightView;
